import { createContext, useContext, useCallback, useMemo, useState, ReactNode } from "react";

/**
 * Context for row-level edit mode.
 * Tracks which specific rows are currently being edited using a Set of row IDs.
 * Double-click a row to enter edit mode for that row only.
 *
 * When a row is NOT in edit mode: Cells render as plain text for performance
 * When a row IS in edit mode: Cells render full input components for editing
 */

interface EditModeContextValue {
	editingRowIds: Set<string>;
	isRowInEditMode: (rowId: string) => boolean;
	toggleRowEdit: (rowId: string) => void;
	exitRowEdit: (rowId: string) => void;
	exitAllEditMode: () => void;
	enterAllEditMode: (rowIds: string[]) => void;
}

const EditModeContext = createContext<EditModeContextValue | null>(null);

interface EditModeProviderProps {
	children: ReactNode;
	initialRowIds?: string[];
}

export function EditModeProvider({ children, initialRowIds }: EditModeProviderProps) {
	const [editingRowIds, setEditingRowIds] = useState<Set<string>>(() => new Set(initialRowIds ?? []));

	const isRowInEditMode = useCallback(
		(rowId: string) => {
			return editingRowIds.has(rowId);
		},
		[editingRowIds],
	);

	const toggleRowEdit = useCallback((rowId: string) => {
		setEditingRowIds((prev) => {
			const next = new Set(prev);
			if (next.has(rowId)) next.delete(rowId);
			else next.add(rowId);
			return next;
		});
	}, []);

	const exitRowEdit = useCallback((rowId: string) => {
		setEditingRowIds((prev) => {
			if (!prev.has(rowId)) return prev;
			const next = new Set(prev);
			next.delete(rowId);
			return next;
		});
	}, []);

	const exitAllEditMode = useCallback(() => {
		setEditingRowIds((prev) => (prev.size === 0 ? prev : new Set()));
	}, []);

	const enterAllEditMode = useCallback((rowIds: string[]) => {
		setEditingRowIds(new Set(rowIds));
	}, []);

	const value = useMemo(
		() => ({
			editingRowIds,
			isRowInEditMode,
			toggleRowEdit,
			exitRowEdit,
			exitAllEditMode,
			enterAllEditMode,
		}),
		[editingRowIds, isRowInEditMode, toggleRowEdit, exitRowEdit, exitAllEditMode, enterAllEditMode],
	);

	return <EditModeContext.Provider value={value}>{children}</EditModeContext.Provider>;
}

export function useEditModeContext(): EditModeContextValue {
	const ctx = useContext(EditModeContext);
	if (!ctx) throw new Error("useEditModeContext must be used within EditModeProvider");
	return ctx;
}

/**
 * Convenience hook for cell components to check if a specific row is in edit mode.
 * Use this in Cell components to determine rendering mode.
 */
export function useIsRowInEditMode(rowId: string): boolean {
	const { isRowInEditMode } = useEditModeContext();
	return isRowInEditMode(rowId);
}

/**
 * @deprecated Use useIsRowInEditMode(rowId) instead for row-level edit mode.
 * This hook is kept for backward compatibility but always returns false.
 */
export function useEditMode(): boolean {
	// Return false since global edit mode is no longer used
	// Components should use useIsRowInEditMode(rowId) instead
	return false;
}
